import { z } from 'zod';

export const layoutViewTypes = ['swimlane', 'timeline', 'table'] as const;
export const layoutViewTypeSchema = z.enum(layoutViewTypes);

export const dateHeaderTypes = [
  'startDate:week',
  'startDate:month',
  'startDate:quarter',
  'startDate:half',
  'startDate:year',
  'endDate:week',
  'endDate:month',
  'endDate:quarter',
  'endDate:half',
  'endDate:year',
] as const;

export const headerDirections = ['horizontal', 'vertical'] as const;
export const headerDirectionSchema = z.enum(headerDirections);

export const themes = ['standard', 'compact'] as const;
export const themeSchema = z.enum(themes);

export const headerTypes = ['status', 'parentItem', 'team', 'createdBy'] as const;
export const allHeaderTypes = [...headerTypes, ...dateHeaderTypes] as const;
export const headerLabelsType = [...headerTypes, 'itemDate'] as const;

export type HeaderLabelsType = (typeof headerLabelsType)[number];
export type HeaderType = (typeof headerLabelsType)[number];

export const HEADER_TYPE_TRANSLATIONS = {
  status: 'roadmapping.layout.headers.fields.status',
  parentItem: 'roadmapping.layout.headers.fields.parentItem',
  team: 'roadmapping.layout.headers.fields.team',
  itemDate: 'roadmapping.layout.headers.fields.date',
  createdBy: 'roadmapping.layout.headers.fields.createdBy',
} as const satisfies Record<HeaderLabelsType, string>;

export const allowedHeaderCustomFieldTypes = ['PICKLIST', 'MULTI_PICKLIST'] as const;
export type AllowedCustomFieldType = (typeof allowedHeaderCustomFieldTypes)[number];

export const layoutHeaderSchema = z.string().refine(
  (val) => {
    if (allHeaderTypes.includes(val as (typeof allHeaderTypes)[number])) {
      return true;
    }
    return /^[1-9]\d*$/.test(val) ? Number.parseInt(val, 10) <= Number.MAX_SAFE_INTEGER : false;
  },
  {
    error: `Invalid header specified. Header must be one of ['${allHeaderTypes.join("', '")}'] or a custom field id.`,
  },
);

export const layoutHeaderDTOSchema = z.nullable(layoutHeaderSchema);
export const layoutHeaderUpsertDTOSchema = z.optional(layoutHeaderDTOSchema);

export const colorConfigSchema = z
  .object({
    mode: z.enum(['baseColor', 'palette']),
    baseColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    paletteId: z.string().optional(),
    colorByField: z.string().nullable().optional(),
    itemColorMap: z.record(z.string(), z.number()).optional(),
    matchHeaderColors: z.boolean().optional(),
    headerColorOrder: z.array(z.string()).optional(),
    desaturatedHeaderColorIndex: z.number().min(0).max(2).optional(),
  })
  .nullable()
  .optional();

export type ColorConfig = z.infer<typeof colorConfigSchema>;

export const baseLayoutDTOSchema = z.object({
  itemLevelId: z.number(),
  viewId: z.number().int().positive(),

  // Allow for header1 and header2 to be null
  header1: layoutHeaderDTOSchema,
  header2: layoutHeaderDTOSchema,

  headerDirection: headerDirectionSchema,
  theme: themeSchema,

  showEmptyHeaders: z.boolean().default(false),

  colorConfig: colorConfigSchema,

  // Label by headers for card labels
  labelByHeader1: layoutHeaderDTOSchema,
  labelByHeader2: layoutHeaderDTOSchema,

  // Header ordering - maps to header1 (column) and header2 (row)
  columnHeaderOrder: z.array(z.string()).optional(),
  rowHeaderOrder: z.array(z.string()).optional(),
});

export const swimlaneLayoutDTOSchema = z.object({
  ...baseLayoutDTOSchema.shape,
  viewType: z.literal('swimlane'),
});

export type SwimlaneLayoutDTO = z.infer<typeof swimlaneLayoutDTOSchema>;

export const timelineLayoutDTOSchema = z.object({
  ...baseLayoutDTOSchema.shape,
  viewType: z.literal('timeline'),
});

export type TimelineLayoutDTO = z.infer<typeof timelineLayoutDTOSchema>;

export const tableLayoutDTOSchema = z.object({
  ...baseLayoutDTOSchema.shape,
  viewType: z.literal('table'),
});

export type TableLayoutDTO = z.infer<typeof tableLayoutDTOSchema>;

export const layoutDTOSchema = z.discriminatedUnion('viewType', [
  swimlaneLayoutDTOSchema,
  timelineLayoutDTOSchema,
  tableLayoutDTOSchema,
]);

export type LayoutDTO = z.infer<typeof layoutDTOSchema>;

export const baseLayoutUpsertDTOSchema = z.object({
  viewType: layoutViewTypeSchema,
  itemLevelId: z.number().int().positive(),
  header1: z.optional(layoutHeaderDTOSchema),
  header2: z.optional(layoutHeaderDTOSchema),
  headerDirection: headerDirectionSchema.optional(),
  showEmptyHeaders: z.boolean().optional(),
  colorConfig: colorConfigSchema,
  labelByHeader1: z.optional(layoutHeaderDTOSchema),
  labelByHeader2: z.optional(layoutHeaderDTOSchema),
});

export const upsertSwimlaneLayoutDTOSchema = z.object({
  ...baseLayoutUpsertDTOSchema.shape,
  viewType: z.literal('swimlane'),
  theme: themeSchema.optional(),
});

export const upsertTimelineLayoutDTOSchema = z.object({
  ...baseLayoutUpsertDTOSchema.shape,
  viewType: z.literal('timeline'),
});

export const upsertTableLayoutDTOSchema = z.object({
  ...baseLayoutUpsertDTOSchema.shape,
  viewType: z.literal('table'),
});

export const layoutUpsertDTOSchema = z
  .discriminatedUnion('viewType', [
    upsertSwimlaneLayoutDTOSchema,
    upsertTimelineLayoutDTOSchema,
    upsertTableLayoutDTOSchema,
  ])
  .superRefine((data, ctx) => {
    // Prevent duplicate header selections - header1 and header2 must be different
    if (data.header1 && data.header1 === data.header2) {
      ctx.addIssue({
        code: 'custom',
        message: 'Header 1 and Header 2 cannot be the same',
        path: ['header2'],
      });
    }
  });

export type LayoutUpsertDTO = z.infer<typeof layoutUpsertDTOSchema>;

export const headerOrderDTOSchema = z.object({
  headerFieldId: layoutHeaderDTOSchema,
  orderedHeaderIds: z.array(z.string()),
});

export type HeaderOrderDTO = z.infer<typeof headerOrderDTOSchema>;

export const headerOrderUpsertDTOSchema = z.object({
  orderedHeaderIds: z.array(z.string()),
});

export type HeaderOrderUpsertDTO = z.infer<typeof headerOrderUpsertDTOSchema>;

export const headerHiddenDTOSchema = z.object({
  headerFieldId: layoutHeaderDTOSchema,
  hiddenHeaderIds: z.array(z.string()),
});

export const headerHiddenUpsertDTOSchema = z.object({
  headerId: z.string(),
});

export type HeaderHiddenDTO = z.infer<typeof headerHiddenDTOSchema>;

export const headerConfigDTOSchema = headerOrderDTOSchema.extend(headerHiddenDTOSchema.shape);
export type HeaderConfigDTO = z.infer<typeof headerConfigDTOSchema>;
